import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  Button,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
} from '@material-ui/core';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Edit } from '@material-ui/icons';
import AddIcon from '@mui/icons-material/Add';
import TablePaginationActions from '../../../components/Common/Table/TablePaginationActions';
import TableLoader from '../../../components/TableLoader';

const styles = makeStyles(() => ({
  tableContainerStyle: {
    boxShadow: 'unset',
  },
  tableRowStyle: {
    '& .MuiTableCell-root': {
      padding: '4px',
      borderBottom: '1px solid #B9A4BC',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  footerRowStyle: {
    '& .MuiTableCell-root': {
      padding: '4px',
      border: '0px',
    },
  },
  headerTextStyles: {
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '16.94px',
  },
  paginationStyle: {
    '& .MuiTablePagination-selectRoot': {
      marginRight: '25px',
      marginLeft: '15px',
    },
    '& .MuiTablePagination-select': {
      paddingLeft: '0px',
      borderBottom: '1px solid',
      paddingBottom: '1px',
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '16.41px',
    },
    '& .MuiTablePagination-caption': {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '16.41px',
      paddingTop: '5px',
    },
  },
  addIconStyles: {
    '& .MuiButton-startIcon': {
      marginRight: '3px',
    },
  },
}));

const EditOrganizations = props => {
  const {
    killshotOrgs,
    isLoading,
    clearOrgState,
    setCreateEditOrgDisable,
    setIncludePrimeBrokerage,
    setIsEdit,
    setOrgState,
  } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const classes = styles();

  const headerArray = ['Organizations', 'Tag', 'Prime Brokerage', 'Add New'];
  const maxVisbleRows = 5;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * maxVisbleRows - killshotOrgs.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (orgId, name, tag, includeInPrime) => {
    setOrgState({
      id: orgId,
      orgName: name,
      orgTag: tag,
    });
    setIsEdit(true);
    setIncludePrimeBrokerage(includeInPrime);
    setCreateEditOrgDisable(false);
  };

  const addNewOnClick = () => {
    clearOrgState();
    setCreateEditOrgDisable(false);
  };

  const headerComponent = headerName => {
    return (
      <TableCell
        align="left"
        style={
          // eslint-disable-next-line no-nested-ternary
          headerName === 'Organizations'
            ? { paddingLeft: '20px', width: '34%' }
            : headerName === 'Add New'
            ? { width: '20%' }
            : { width: '23%' }
        }
      >
        {headerName === 'Add New' ? (
          <Button
            className={classes.addIconStyles}
            onClick={addNewOnClick}
            startIcon={<AddIcon />}
            size="small"
            variant="text"
            color="primary"
          >
            <Typography style={{ color: '#BA68C8', textTransform: 'none' }} className={classes.headerTextStyles}>
              {headerName}
            </Typography>
          </Button>
        ) : (
          <Typography className={classes.headerTextStyles}>{headerName}</Typography>
        )}
      </TableCell>
    );
  };

  const tableContent = row => {
    return (
      <TableRow
        key={row.id}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        className={classes.tableRowStyle}
      >
        <TableCell style={{ paddingLeft: '20px' }}>
          <Typography className={classes.headerTextStyles} style={{ fontWeight: '500' }}>
            {row.name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className={classes.headerTextStyles} style={{ fontWeight: '500' }}>
            {row.tag}
          </Typography>
        </TableCell>
        <TableCell style={{ paddingLeft: '40px' }}>{row.includeInPrime ? <CheckIcon /> : <CloseIcon />}</TableCell>
        <TableCell style={{ paddingLeft: '10px' }}>
          <Button
            onClick={() => {
              handleEdit(row.id, row.name, row.tag, row.includeInPrime);
            }}
            startIcon={<Edit />}
            size="small"
            variant="contained"
            color="primary"
          >
            <Typography className={classes.headerTextStyles} style={{ fontWeight: '500' }}>
              Edit
            </Typography>
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      {!isLoading ? (
        <Paper className={classes.tableContainerStyle}>
          <TableContainer style={{ maxHeight: '230px' }}>
            <Table aria-label="simple table" style={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow className={classes.tableRowStyle}>{headerArray.map(item => headerComponent(item))}</TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? killshotOrgs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : killshotOrgs
                ).map(row => tableContent(row))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 38.75 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            count={killshotOrgs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                'aria-label': 'killshotOrgs per page',
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            labelRowsPerPage="Organizations per page:"
            className={classes.paginationStyle}
            component="div"
            style={{ paddingTop: '15px' }}
          />
        </Paper>
      ) : (
        <TableLoader style={{ width: '100%', height: '100%' }} />
      )}
    </>
  );
};

EditOrganizations.propTypes = {
  killshotOrgs: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  clearOrgState: PropTypes.array.isRequired,
  setCreateEditOrgDisable: PropTypes.func.isRequired,
  setIncludePrimeBrokerage: PropTypes.func.isRequired,
  setIsEdit: PropTypes.func.isRequired,
  setOrgState: PropTypes.func.isRequired,
};
export default EditOrganizations;
